import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Receipt, Plus, Search, FileText, Loader } from 'lucide-react';
import { CUENTAS_COBRO, RESUMEN_CUENTAS_COBRO } from '../graphql/cuentasCobro';
import ModalGenerarCuentas from '../components/cuentasCobro/ModalGenerarCuentas';
import ModalCuentaDetalle from '../components/cuentasCobro/ModalCuentaDetalle';
import ModalRegistrarPago from '../components/cuentasCobro/ModalRegistrarPago';
import Paginacion from '../components/factubox/Paginacion';
import './CuentasCobro.css';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function formatMoneda(v) {
  if (v == null) return '$0';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

function CuentasCobro() {
  const hoy = new Date();
  // Por defecto mostrar el mes siguiente (que es lo más común para generar cuentas)
  const mesDefault = hoy.getDate() >= 25 ? (hoy.getMonth() + 2 > 12 ? 1 : hoy.getMonth() + 2) : hoy.getMonth() + 1;
  const anioDefault = hoy.getDate() >= 25 && hoy.getMonth() + 2 > 12 ? hoy.getFullYear() + 1 : hoy.getFullYear();

  const [periodoMes, setPeriodoMes] = useState(mesDefault);
  const [periodoAnio, setPeriodoAnio] = useState(anioDefault);
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [busquedaLocal, setBusquedaLocal] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const LIMIT = 30;

  // Modales
  const [modalGenerar, setModalGenerar] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(null);
  const [modalPago, setModalPago] = useState(null);

  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (busquedaLocal !== busqueda) {
        setBusqueda(busquedaLocal);
        setPaginaActual(1);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [busquedaLocal]);

  const { data, loading, refetch } = useQuery(CUENTAS_COBRO, {
    variables: {
      periodo_mes: periodoMes,
      periodo_anio: periodoAnio,
      estado: estadoFiltro || null,
      busqueda: busqueda || null,
      limit: LIMIT,
      offset: (paginaActual - 1) * LIMIT,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: resumenData } = useQuery(RESUMEN_CUENTAS_COBRO, {
    variables: { periodo_mes: periodoMes, periodo_anio: periodoAnio },
    fetchPolicy: 'cache-and-network',
  });

  const cuentas = data?.cuentasCobro?.cuentas || [];
  const total = data?.cuentasCobro?.total || 0;
  const totalPaginas = Math.ceil(total / LIMIT);
  const resumen = resumenData?.resumenCuentasCobro;

  const handlePeriodoChange = (mes, anio) => {
    setPeriodoMes(mes);
    setPeriodoAnio(anio);
    setPaginaActual(1);
  };

  const refetchAll = () => refetch();

  return (
    <div className="cuentas-cobro-page">
      <div className="cuentas-cobro-header">
        <div className="cuentas-cobro-header__titulo">
          <Receipt size={28} />
          <div>
            <h1>Cuentas de Cobro</h1>
            <p>Facturación mensual de hospedaje a soldados</p>
          </div>
        </div>
        <button className="btn-generar-cuentas" onClick={() => setModalGenerar(true)}>
          <Plus size={18} />
          Generar Cuentas del Mes
        </button>
      </div>

      {/* Selector de periodo */}
      <div className="cuentas-cobro-periodo">
        <div className="periodo-selector">
          <label>Mes</label>
          <select value={periodoMes} onChange={e => handlePeriodoChange(Number(e.target.value), periodoAnio)}>
            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div className="periodo-selector">
          <label>Año</label>
          <select value={periodoAnio} onChange={e => handlePeriodoChange(periodoMes, Number(e.target.value))}>
            {[2024, 2025, 2026, 2027, 2028].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* Resumen cards */}
      {resumen && (
        <div className="cuentas-cobro-resumen">
          <div className="resumen-card resumen-card--emitido">
            <span className="resumen-card__label">Total Emitido</span>
            <span className="resumen-card__valor">{formatMoneda(resumen.total_emitido)}</span>
            <span className="resumen-card__detalle">{resumen.total_cuentas} cuentas</span>
          </div>
          <div className="resumen-card resumen-card--cobrado">
            <span className="resumen-card__label">Cobrado</span>
            <span className="resumen-card__valor">{formatMoneda(resumen.total_cobrado)}</span>
            <span className="resumen-card__detalle">{resumen.porcentaje_cobro}% recaudado</span>
          </div>
          <div className="resumen-card resumen-card--pendiente">
            <span className="resumen-card__label">Pendiente</span>
            <span className="resumen-card__valor">{formatMoneda(resumen.total_pendiente)}</span>
            <span className="resumen-card__detalle">
              {resumen.por_estado.emitidas + resumen.por_estado.parciales} por cobrar
            </span>
          </div>
          <div className="resumen-card resumen-card--cobro">
            <span className="resumen-card__label">% Cobro</span>
            <span className="resumen-card__valor">{resumen.porcentaje_cobro}%</span>
            <div className="barra-progreso">
              <div className="barra-progreso__fill" style={{ width: `${resumen.porcentaje_cobro}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="cuentas-cobro-filtros">
        <div className="filtro-grupo">
          <label>Estado</label>
          <select value={estadoFiltro} onChange={e => { setEstadoFiltro(e.target.value); setPaginaActual(1); }}>
            <option value="">Todos</option>
            <option value="emitida">Emitida</option>
            <option value="parcial">Parcial</option>
            <option value="pagada">Pagada</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>
        <div className="filtro-grupo filtro-grupo--busqueda">
          <label>Buscar</label>
          <div className="input-with-icon">
            <Search size={16} />
            <input
              type="text"
              placeholder="Nombre, documento, código..."
              value={busquedaLocal}
              onChange={e => setBusquedaLocal(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="cuentas-cobro-tabla-container">
        {loading && (
          <div className="loading-overlay">
            <Loader size={32} className="spinner" />
            <p>Cargando...</p>
          </div>
        )}

        {!loading && cuentas.length === 0 && (
          <div className="empty-state">
            <FileText size={48} color="#9ca3af" />
            <p className="empty-state__title">No hay cuentas de cobro</p>
            <p className="empty-state__subtitle">
              Genera las cuentas del mes con el botón "Generar Cuentas del Mes"
            </p>
          </div>
        )}

        {cuentas.length > 0 && (
          <>
            <div className="tabla-wrapper">
              <table className="tabla-cuentas">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Soldado</th>
                    <th>Grado</th>
                    <th>Hab/Cama</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">Pagado</th>
                    <th className="text-right">Saldo</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cuentas.map(cc => (
                    <tr key={cc.id}>
                      <td className="td-codigo">{cc.codigo}</td>
                      <td>
                        <div className="soldado-info">
                          <strong>{cc.nombre_soldado}</strong>
                          <small>{cc.numero_documento}</small>
                        </div>
                      </td>
                      <td>{cc.grado_militar_nombre || '-'}</td>
                      <td>{cc.habitacion_numero}/{cc.cama_numero || '-'}</td>
                      <td className="text-right">{formatMoneda(cc.monto_total)}</td>
                      <td className="text-right">{formatMoneda(cc.monto_pagado)}</td>
                      <td className="text-right font-bold">{formatMoneda(cc.saldo_pendiente)}</td>
                      <td>
                        <span className={`badge badge--${cc.estado}`}>
                          {cc.estado.charAt(0).toUpperCase() + cc.estado.slice(1)}
                        </span>
                      </td>
                      <td>
                        <div className="acciones-grupo">
                          <button
                            className="btn-accion btn-accion--ver"
                            title="Ver detalle"
                            onClick={() => setModalDetalle(cc.id)}
                          >
                            <FileText size={15} />
                          </button>
                          {cc.estado !== 'pagada' && cc.estado !== 'cancelada' && (
                            <button
                              className="btn-accion btn-accion--pagar"
                              title="Registrar pago"
                              onClick={() => setModalPago(cc)}
                            >
                              $
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="tabla-footer">
              <p><strong>{total}</strong> {total === 1 ? 'cuenta' : 'cuentas'} de cobro</p>
            </div>

            {totalPaginas > 1 && (
              <Paginacion
                paginaActual={paginaActual}
                totalPaginas={totalPaginas}
                totalRegistros={total}
                registrosPorPagina={LIMIT}
                onCambioPagina={p => { setPaginaActual(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              />
            )}
          </>
        )}
      </div>

      {/* Modales */}
      {modalGenerar && (
        <ModalGenerarCuentas
          periodoMes={periodoMes}
          periodoAnio={periodoAnio}
          onClose={() => setModalGenerar(false)}
          onSuccess={() => { setModalGenerar(false); refetchAll(); }}
        />
      )}

      {modalDetalle && (
        <ModalCuentaDetalle
          cuentaId={modalDetalle}
          onClose={() => setModalDetalle(null)}
          onPagoRegistrado={refetchAll}
        />
      )}

      {modalPago && (
        <ModalRegistrarPago
          cuenta={modalPago}
          onClose={() => setModalPago(null)}
          onSuccess={() => { setModalPago(null); refetchAll(); }}
        />
      )}
    </div>
  );
}

export default CuentasCobro;
